import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';

/**
 * VoyageContext — Phase UX globale (Feb 2026, étendu Mission 3).
 *
 * Source unique pour les filtres métier transverses entre Dashboard,
 * Voyages, Paiements, Régularisations, Rapports et FournisseursHub.
 *
 * EXPOSE :
 *   scope          - 'all' | 'mine' | 'external'                (rétro-compat)
 *   scopeParams    - { voyage_filter: scope }                   (rétro-compat)
 *   setScope       - (s) => void                                (rétro-compat)
 *   currentVoyage  - { id, ref, nom } | null                    voyage actif
 *   currentVoyageGroup - string (code) | null                   groupe voyage actif
 *   currentCurrency - 'DZD' | 'EUR' | …  | null                 devise filtre
 *   currentPeriod  - { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD' } | null
 *   setCurrentVoyage / setCurrentVoyageGroup / setCurrentCurrency / setCurrentPeriod
 *   filters        - { ...derived params for backend API calls }
 *   resetFilters   - () => void
 *
 * CONTRAINTES :
 *   - UI-only (aucune logique comptable ni fetch centralisé)
 *   - Memoization stricte pour éviter rerenders inutiles
 *   - Persistance light via localStorage (1 clé)
 *   - Compatible existant : useVoyageScope() continue de fonctionner sans break
 */

const SCOPES = ['all', 'mine', 'external'];
const STORAGE_KEY = 'voyageContext';

const DEFAULT_STATE = {
  scope: 'all',
  currentVoyage: null,
  currentVoyageGroup: null,
  currentCurrency: null,
  currentPeriod: null,
};

const VoyageContext = createContext({
  ...DEFAULT_STATE,
  scopeParams: { voyage_filter: 'all' },
  filters: {},
  setScope: () => {},
  setCurrentVoyage: () => {},
  setCurrentVoyageGroup: () => {},
  setCurrentCurrency: () => {},
  setCurrentPeriod: () => {},
  resetFilters: () => {},
});

function loadInitial() {
  try {
    if (typeof window === 'undefined') return { ...DEFAULT_STATE };
    const raw = window.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_STATE };
    const parsed = JSON.parse(raw);
    return {
      scope: SCOPES.includes(parsed.scope) ? parsed.scope : 'all',
      currentVoyage: parsed.currentVoyage ?? null,
      currentVoyageGroup: parsed.currentVoyageGroup ?? null,
      currentCurrency: parsed.currentCurrency ?? null,
      currentPeriod: parsed.currentPeriod ?? null,
    };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

function persist(state) {
  try {
    window.localStorage?.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // silent
  }
}

export const VoyageProvider = ({ children }) => {
  const [state, setState] = useState(loadInitial);

  // Setter générique avec persistance immédiate
  const patchState = useCallback((patch) => {
    setState((prev) => {
      const next = { ...prev, ...patch };
      persist(next);
      return next;
    });
  }, []);

  const setScope = useCallback((next) => {
    if (!SCOPES.includes(next)) return;
    patchState({ scope: next });
  }, [patchState]);

  const setCurrentVoyage = useCallback((v) => {
    patchState({ currentVoyage: v ?? null });
  }, [patchState]);

  const setCurrentVoyageGroup = useCallback((code) => {
    patchState({ currentVoyageGroup: code ? String(code).toUpperCase() : null });
  }, [patchState]);

  const setCurrentCurrency = useCallback((cur) => {
    patchState({ currentCurrency: cur ?? null });
  }, [patchState]);

  const setCurrentPeriod = useCallback((period) => {
    if (!period) return patchState({ currentPeriod: null });
    const { start, end } = period;
    if (!start && !end) return patchState({ currentPeriod: null });
    patchState({ currentPeriod: { start: start || null, end: end || null } });
  }, [patchState]);

  const resetFilters = useCallback(() => {
    const next = { ...DEFAULT_STATE };
    persist(next);
    setState(next);
  }, []);

  // Paramètres dérivés pour les appels API : tout ce qui est non-null
  const scopeParams = useMemo(() => ({ voyage_filter: state.scope }), [state.scope]);

  const filters = useMemo(() => {
    const f = { voyage_filter: state.scope };
    if (state.currentVoyage?.id) f.voyage_id = state.currentVoyage.id;
    if (state.currentVoyageGroup) f.voyage_group_code = state.currentVoyageGroup;
    if (state.currentCurrency) f.currency = state.currentCurrency;
    if (state.currentPeriod?.start) f.period_start = state.currentPeriod.start;
    if (state.currentPeriod?.end) f.period_end = state.currentPeriod.end;
    return f;
  }, [state.scope, state.currentVoyage, state.currentVoyageGroup, state.currentCurrency, state.currentPeriod]);

  const value = useMemo(() => ({
    ...state,
    scopeParams,
    filters,
    setScope,
    setCurrentVoyage,
    setCurrentVoyageGroup,
    setCurrentCurrency,
    setCurrentPeriod,
    resetFilters,
  }), [state, scopeParams, filters, setScope, setCurrentVoyage, setCurrentVoyageGroup, setCurrentCurrency, setCurrentPeriod, resetFilters]);

  return <VoyageContext.Provider value={value}>{children}</VoyageContext.Provider>;
};

// Hook principal — expose tout
export const useVoyageContext = () => useContext(VoyageContext);

// Hook simplifié — rétro-compat avec useVoyageScope (utilisé partout déjà)
export const useVoyageScope = () => {
  const ctx = useContext(VoyageContext);
  return { scope: ctx.scope, setScope: ctx.setScope, scopeParams: ctx.scopeParams };
};

/**
 * Sélecteur UX standardisé du SCOPE voyage (all/mine/external).
 * Réutilisable dans tous les headers de modules.
 */
export const VoyageScopeSelector = ({ className = '' }) => {
  const { scope, setScope } = useVoyageScope();
  const options = [
    { value: 'all', label: 'Tous voyages' },
    { value: 'mine', label: 'Mes voyages' },
    { value: 'external', label: 'Voyages externes' },
  ];
  return (
    <div
      data-testid="voyage-scope-selector"
      className={`inline-flex items-center rounded-lg border border-zinc-200 bg-white p-0.5 ${className}`}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          data-testid={`voyage-scope-${o.value}`}
          onClick={() => setScope(o.value)}
          className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
            scope === o.value
              ? 'bg-zinc-900 text-white'
              : 'text-zinc-600 hover:bg-zinc-100'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
};

/**
 * Indicateur "filtres actifs" — à afficher dans les headers pour signaler
 * à l'utilisateur qu'un filtre global est posé. Optionnel.
 */
export const VoyageFiltersBadge = () => {
  const { currentVoyageGroup, currentCurrency, currentPeriod, resetFilters } = useVoyageContext();
  const items = [];
  if (currentVoyageGroup) items.push(`Voyage: ${currentVoyageGroup}`);
  if (currentCurrency) items.push(`Devise: ${currentCurrency}`);
  if (currentPeriod?.start || currentPeriod?.end) {
    items.push(`Période: ${currentPeriod.start || '…'} → ${currentPeriod.end || '…'}`);
  }
  if (items.length === 0) return null;
  return (
    <div data-testid="voyage-filters-badge" className="inline-flex items-center gap-2 px-2 py-1 rounded-md text-[11px] bg-indigo-50 border border-indigo-200 text-indigo-800">
      <span className="font-semibold">Filtres actifs :</span>
      <span>{items.join(' · ')}</span>
      <button
        type="button"
        data-testid="voyage-filters-clear"
        onClick={resetFilters}
        className="ml-1 px-1.5 py-0.5 rounded hover:bg-indigo-100 font-semibold"
      >
        Réinitialiser
      </button>
    </div>
  );
};
